import React, { useState } from 'react'
import styled from 'styled-components'
import Button from './Button'
import theme from '../styles/theme'
import { 
  searchUserByEmail, 
  getUserBySessionMId, 
  getAllPointBalances, 
  getUserOffers,
  updateUserProfile,
  depositMergedPoints,
  issueOffersBulk,
  forgetUser,
  POINT_ACCOUNTS,
  MERGE_POINT_SOURCE_ID
} from '../services/api'

const MergeContainer = styled.div`
  background: ${theme.color.background.frame};
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 16px ${theme.color.shadow.medium}, 
              0 4px 8px ${theme.color.shadow.light};
  margin-bottom: 2rem;
`

const MergeTitle = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`

const InputSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled.label`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const Input = styled.input<{ readOnly?: boolean }>`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 8px;
  font-size: 1.4rem;
  background: ${props => props.readOnly ? theme.color.background.lightGrey : theme.color.background.light};
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  transition: all 0.2s ease;
  color: ${props => props.readOnly ? theme.color.text.grey : theme.color.text.base};
  cursor: ${props => props.readOnly ? 'not-allowed' : 'text'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.readOnly ? theme.color.border.light : theme.color.accent.primary};
    box-shadow: ${props => props.readOnly 
      ? 'inset 0 2px 4px ' + theme.color.shadow.light
      : `inset 0 2px 4px ${theme.color.shadow.light}, 0 0 0 3px rgba(215, 0, 54, 0.1)`};
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`

const DangerButton = styled(Button)`
  background: #d32f2f;
  
  &:hover {
    background: #ef5350;
    box-shadow: 0 0 16px rgba(239, 83, 80, 0.25);
  }
  
  &:disabled {
    background: #666;
    box-shadow: none;
  }
`

const SectionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ProfileCard = styled.div`
  border: 1px solid ${theme.color.border.light};
  border-radius: 12px;
  padding: 1.5rem;
  background: ${theme.color.background.light};
  box-shadow: 0 2px 6px ${theme.color.shadow.light};
`

const CardTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const FieldSet = styled.fieldset`
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  margin-bottom: 1rem;
  padding: 1rem;
  background: ${theme.color.background.frame};
`

const Legend = styled.legend`
  font-weight: 600;
  color: ${theme.color.accent.primary};
  padding: 0 0.5rem;
`

const FieldRow = styled.div<{ withCheckbox?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.withCheckbox ? '24px 150px 1fr' : '150px 1fr'};
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid ${theme.color.border.light};
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
`

const FieldLabel = styled.div`
  font-weight: 500;
  color: ${theme.color.text.base};
  font-size: 1.2rem;
`

const FieldValue = styled.div`
  color: ${theme.color.text.subtext};
  font-size: 1.2rem;
  word-break: break-word;
`

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
`

const LogContainer = styled.div`
  background: #0b1020;
  color: #c5cae9;
  padding: 1rem;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 1.2rem;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  margin-bottom: 1rem;
`

const PayloadPreview = styled.pre`
  background: #0b1020;
  color: #c5cae9;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1.1rem;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 1rem;
`

const WarningMessage = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #ffeaa7;
  margin-bottom: 1rem;
  font-weight: 500;
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`

const SuccessMessage = styled.div`
  background: #e6f7e6;
  color: #2d7d2d;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`

// Field definitions from the Electron app
const BASIC_FIELDS = [
  "last_name", "first_name", "external_id", "email", "dob",
  "address", "address2", "city", "zip", "state", "country", "phone_number"
]

const TIER_FIELDS = ["tier", "tier_system"]

const PREF_FIELDS = [
  "home_studio", "marketing_opt_in", "top_studio", "favoriteShake",
  "topSize", "bottomSize", "shoeSize", "heardAboutOn"
]

const ALL_FIELDS = [...BASIC_FIELDS, ...TIER_FIELDS, ...PREF_FIELDS]

interface CustomerMergeProps {
  userData?: any
}

interface ProfileData {
  [key: string]: any
}

interface VictimCheckStates {
  [userId: string]: {
    [field: string]: boolean
  }
}

const CustomerMerge: React.FC<CustomerMergeProps> = ({ userData }) => {
  const [survivorEmail, setSurvivorEmail] = useState(userData?.user?.email || '')
  const [victimEmails, setVictimEmails] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [log, setLog] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Profile data
  const [survivorProfile, setSurvivorProfile] = useState<ProfileData | null>(null)
  const [survivorId, setSurvivorId] = useState<string | null>(null)
  const [victimProfiles, setVictimProfiles] = useState<{[userId: string]: ProfileData}>({})
  const [victimIdToEmail, setVictimIdToEmail] = useState<{[userId: string]: string}>({})
  const [victimCheckStates, setVictimCheckStates] = useState<VictimCheckStates>({})
  
  // Points and offers data
  const [survivorBalances, setSurvivorBalances] = useState<{[accountId: string]: number}>({})
  const [victimBalances, setVictimBalances] = useState<{[userId: string]: {[accountId: string]: number}}>({})
  const [survivorOffers, setSurvivorOffers] = useState<any[]>([])
  const [victimOffers, setVictimOffers] = useState<{[userId: string]: any[]}>({})
  const [victimOfferChecks, setVictimOfferChecks] = useState<{[userId: string]: {[offerId: string]: boolean}}>({})
  
  const [payloadPreview, setPayloadPreview] = useState('')

  // Update survivor email when userData changes
  React.useEffect(() => {
    if (userData?.user?.email) {
      setSurvivorEmail(userData.user.email)
    }
  }, [userData?.user?.email])

  // Utility functions
  const addLog = (message: string) => {
    setLog(prev => prev + message + '\n')
  }

  const clearAll = () => {
    setSurvivorProfile(null)
    setSurvivorId(null)
    setVictimProfiles({})
    setVictimIdToEmail({})
    setVictimCheckStates({})
    setSurvivorBalances({})
    setVictimBalances({})
    setSurvivorOffers([])
    setVictimOffers({})
    setVictimOfferChecks({})
    setPayloadPreview('')
    setLog('')
    setError('')
    setSuccess('')
  }

  const getAccountLabel = (accountId: string): string => {
    const labels: {[key: string]: string} = {
      [POINT_ACCOUNTS.SPEND_ON_BARRYS_X]: "Spend on Barry's X",
      [POINT_ACCOUNTS.SOCIAL]: 'Social',
      [POINT_ACCOUNTS.RETAIL_OR_FUEL]: 'Spend on Retail or Fuel',
      [POINT_ACCOUNTS.TAKE_A_CLASS]: 'Take a Class',
      [POINT_ACCOUNTS.TIER_TRACKED]: 'Tier Tracked Point Account'
    }
    return labels[accountId] || accountId
  }

  const formatNumber = (n: number | string): string => {
    const num = Number(n) || 0
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }

  const renderPointsSection = (balances: {[accountId: string]: number}, title: string) => {
    return (
      <FieldSet>
        <Legend>{title} Point Balances</Legend>
        {Object.entries(POINT_ACCOUNTS).map(([key, accountId]) => {
          const balance = balances[accountId] || 0
          const label = getAccountLabel(accountId)
          
          return (
            <FieldRow key={accountId}>
              <FieldLabel>{label}:</FieldLabel>
              <FieldValue>{formatNumber(balance)}</FieldValue>
            </FieldRow>
          )
        })}
      </FieldSet>
    )
  }

  const renderVictimPointsSum = () => {
    if (Object.keys(victimBalances).length === 0) return null

    // Calculate sums across all victims
    const sums: {[accountId: string]: number} = {}
    Object.values(POINT_ACCOUNTS).forEach(accountId => {
      sums[accountId] = 0
    })

    Object.values(victimBalances).forEach(balances => {
      Object.values(POINT_ACCOUNTS).forEach(accountId => {
        sums[accountId] += (balances[accountId] || 0)
      })
    })

    return (
      <FieldSet>
        <Legend>🎯 Total Points from Removed Accounts</Legend>
        {Object.entries(POINT_ACCOUNTS).map(([key, accountId]) => {
          const total = sums[accountId] || 0
          const label = getAccountLabel(accountId)
          
          return (
            <FieldRow key={accountId}>
              <FieldLabel>{label}:</FieldLabel>
              <FieldValue style={{ fontWeight: 'bold', color: total > 0 ? '#2d7d2d' : 'inherit' }}>
                {formatNumber(total)}
              </FieldValue>
            </FieldRow>
          )
        })}
      </FieldSet>
    )
  }

  const handleOfferCheck = (userId: string, offerInstanceId: string, checked: boolean) => {
    setVictimOfferChecks(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [offerInstanceId]: checked
      }
    }))
  }

  const renderOffersSection = (offers: any[], withCheckboxes: boolean = false, userId?: string) => {
    if (!offers || offers.length === 0) {
      return (
        <FieldSet>
          <Legend>No Offers</Legend>
          <FieldRow>
            <FieldLabel>Status:</FieldLabel>
            <FieldValue>No offers found</FieldValue>
          </FieldRow>
        </FieldSet>
      )
    }

    return (
      <FieldSet>
        <Legend>Offers ({offers.length})</Legend>
        {offers.map(offer => {
          const isChecked = withCheckboxes && userId ? 
            (victimOfferChecks[userId]?.[offer.offer_instance_id] || false) : false

          return (
            <FieldRow key={offer.offer_instance_id} withCheckbox={withCheckboxes}>
              {withCheckboxes && userId && (
                <Checkbox
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => handleOfferCheck(userId, offer.offer_instance_id, e.target.checked)}
                />
              )}
              <FieldLabel>{offer.offer_id}:</FieldLabel>
              <FieldValue>
                <div>{offer.name || '(no name)'}</div>
                {offer.acquire_date && (
                  <div style={{ fontSize: '1rem', color: '#666' }}>
                    Acquired: {new Date(offer.acquire_date).toLocaleDateString()}
                  </div>
                )}
                {offer.expiration_date && (
                  <div style={{ fontSize: '1rem', color: '#666' }}>
                    Expires: {new Date(offer.expiration_date).toLocaleDateString()}
                  </div>
                )}
              </FieldValue>
            </FieldRow>
          )
        })}
      </FieldSet>
    )
  }

  const handleVictimFieldChange = (userId: string, field: string, checked: boolean) => {
    setVictimCheckStates(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: checked
      }
    }))
  }

  const renderFieldSection = (
    title: string, 
    fields: string[], 
    profile: ProfileData, 
    withCheckboxes: boolean = false,
    userId?: string
  ) => {
    return (
      <FieldSet key={title}>
        <Legend>{title}</Legend>
        {fields.map(field => {
          const isTierField = TIER_FIELDS.includes(field)
          const value = profile?.[field] ?? ''
          
          return (
            <FieldRow key={field} withCheckbox={withCheckboxes && !isTierField}>
              {withCheckboxes && !isTierField && userId && (
                <Checkbox
                  type="checkbox"
                  checked={victimCheckStates[userId]?.[field] || false}
                  onChange={(e) => handleVictimFieldChange(userId, field, e.target.checked)}
                />
              )}
              <FieldLabel>{field}:</FieldLabel>
              <FieldValue>{String(value)}</FieldValue>
            </FieldRow>
          )
        })}
      </FieldSet>
    )
  }

  const handleFetchProfiles = async () => {
    if (!survivorEmail.trim()) {
      setError('Please enter survivor email')
      return
    }

    setIsLoading(true)
    setError('')
    setSuccess('')
    clearAll()
    
    addLog(`Starting fetch for survivor: ${survivorEmail}`)
    
    try {
      // Fetch survivor profile
      addLog(`Resolving survivor ID for ${survivorEmail}...`)
      const survivorSearch = await searchUserByEmail(survivorEmail)
      
      let survivorUserId: string
      if (survivorSearch.user) {
        survivorUserId = survivorSearch.user.id
      } else if (survivorSearch.users && survivorSearch.users.length > 0) {
        survivorUserId = survivorSearch.users[0].id
      } else {
        throw new Error('Keep user not found')
      }
      
      addLog(`Keep ID: ${survivorUserId}`)
      setSurvivorId(survivorUserId)

      addLog('Fetching keep profile...')
      const survivorData = await getUserBySessionMId(survivorUserId)
      setSurvivorProfile(survivorData.user)
      addLog('Keep profile loaded')

      // Fetch keep points
      addLog('Fetching keep point balances...')
      const survivorPointBalances = await getAllPointBalances(survivorUserId)
      survivorPointBalances[POINT_ACCOUNTS.TIER_TRACKED] = computeTierTracked(survivorPointBalances)
      setSurvivorBalances(survivorPointBalances)
      addLog('Keep balances loaded')

      // Fetch keep offers
      addLog('Fetching keep offers...')
      const survivorOffersList = await getUserOffers(survivorUserId)
      const survivorOffersArray = Array.isArray(survivorOffersList) ? survivorOffersList : []
      setSurvivorOffers(survivorOffersArray)
      addLog(`Keep offers loaded (${survivorOffersArray.length} offers)`)

      // Process remove emails
      const victimEmailList = victimEmails.split(',').map(email => email.trim()).filter(Boolean)

      if (victimEmailList.length > 0) {
        addLog(`Processing ${victimEmailList.length} account(s) to remove...`)
        
        const newVictimProfiles: {[userId: string]: any} = {}
        const newVictimIdToEmail: {[userId: string]: string} = {}
        const newVictimBalances: {[userId: string]: any} = {}
        const newVictimOffers: {[userId: string]: any[]} = {}
        const newVictimCheckStates: {[userId: string]: {[field: string]: boolean}} = {}
        
        for (const victimEmail of victimEmailList) {
          try {
            addLog(`Resolving remove account ID for ${victimEmail}...`)
            const victimSearch = await searchUserByEmail(victimEmail)

            let victimUserId: string
            if (victimSearch.user) {
              victimUserId = victimSearch.user.id
            } else if (victimSearch.users && victimSearch.users.length > 0) {
              victimUserId = victimSearch.users[0].id
            } else {
              throw new Error(`Remove account not found: ${victimEmail}`)
            }

            addLog(`Remove ID: ${victimUserId}`)
            newVictimIdToEmail[victimUserId] = victimEmail

            addLog(`Fetching profile for ${victimEmail}...`)
            const victimData = await getUserBySessionMId(victimUserId)
            newVictimProfiles[victimUserId] = victimData.user

            addLog(`Fetching point balances for ${victimEmail}...`)
            const victimPointBalances = await getAllPointBalances(victimUserId)
            victimPointBalances[POINT_ACCOUNTS.TIER_TRACKED] = computeTierTracked(victimPointBalances)
            newVictimBalances[victimUserId] = victimPointBalances

            addLog(`Fetching offers for ${victimEmail}...`)
            const victimOffersList = await getUserOffers(victimUserId)
            newVictimOffers[victimUserId] = Array.isArray(victimOffersList) ? victimOffersList : []

            // Initialize check states
            newVictimCheckStates[victimUserId] = {}

            addLog(`✅ ${victimEmail} loaded successfully`)

          } catch (err) {
            addLog(`❌ Error loading ${victimEmail}: ${err instanceof Error ? err.message : 'Unknown error'}`)
          }
        }
        
        setVictimProfiles(newVictimProfiles)
        setVictimIdToEmail(newVictimIdToEmail)
        setVictimBalances(newVictimBalances)
        setVictimOffers(newVictimOffers)
        setVictimCheckStates(newVictimCheckStates)
      }
      
      addLog('✅ Fetch completed successfully')
      setSuccess('All profiles and data loaded successfully')
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch profiles'
      setError(errorMessage)
      addLog(`❌ Error: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  const computeTierTracked = (balances: {[accountId: string]: number}) => {
    return (
      (balances[POINT_ACCOUNTS.SOCIAL] || 0) +
      (balances[POINT_ACCOUNTS.SPEND_ON_BARRYS_X] || 0) +
      (balances[POINT_ACCOUNTS.RETAIL_OR_FUEL] || 0) +
      (balances[POINT_ACCOUNTS.TAKE_A_CLASS] || 0)
    )
  }

  const handleMergeProfiles = async () => {
    if (!survivorProfile || !survivorId) {
      setError('No survivor profile loaded. Fetch profiles first.')
      return
    }

    try {
      setIsLoading(true)
      addLog('Starting profile merge...')
      
      // Build merged profile
      const merged = { ...survivorProfile }
      
      Object.entries(victimProfiles).forEach(([userId, victimProfile]) => {
        const checkStates = victimCheckStates[userId] || {}
        
        ALL_FIELDS.forEach(field => {
          if (TIER_FIELDS.includes(field)) return // Skip tier fields
          if (checkStates[field]) {
            merged[field] = victimProfile[field]
            addLog(`Merging ${field} from victim ${victimIdToEmail[userId] || userId}`)
          }
        })
      })

      // Build payload (similar to buildPutPayload from Electron app)
      const payload = buildProfilePayload(merged)
      setPayloadPreview(JSON.stringify(payload, null, 2))
      
      addLog('Sending profile update...')
      const response = await updateUserProfile(survivorId, payload)
      
      addLog('✅ Profile merge completed successfully')
      addLog(JSON.stringify(response, null, 2))
      setSuccess('Profile merge completed successfully')
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Profile merge failed'
      setError(errorMessage)
      addLog(`❌ Profile merge error: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleMergePoints = async () => {
    if (!survivorId) {
      setError('No survivor loaded. Fetch profiles first.')
      return
    }

    try {
      setIsLoading(true)
      addLog('Starting points merge...')
      
      // Calculate sums from all victims
      const sums: {[accountId: string]: number} = {}
      Object.values(POINT_ACCOUNTS).forEach(accountId => {
        sums[accountId] = 0
      })
      
      Object.entries(victimBalances).forEach(([userId, balances]) => {
        const email = victimIdToEmail[userId] || userId
        addLog(`Adding points from victim ${email}`)
        
        Object.values(POINT_ACCOUNTS).forEach(accountId => {
          const amount = balances[accountId] || 0
          sums[accountId] += amount
          if (amount > 0) {
            addLog(`  ${getAccountLabel(accountId)}: +${amount}`)
          }
        })
      })
      
      // Build deposit details
      const depositDetails = Object.entries(sums)
        .filter(([accountId, amount]) => amount > 0)
        .map(([accountId, amount], index) => ({
          point_source_id: MERGE_POINT_SOURCE_ID,
          amount: Math.round(amount * 100) / 100,
          point_account_id: accountId,
          reference_id: `merge_user_${survivorId}`,
          reference_type: "customer_account_merge",
          rank: index
        }))
      
      if (depositDetails.length === 0) {
        addLog('No positive point amounts to transfer')
        setPayloadPreview('No points to transfer')
        return
      }
      
      const payload = {
        retailer_id: "AAB85D10-D11A-4012-8465-C961AC49DC91",
        user_id: survivorId,
        deposit_details: depositDetails,
        allow_partial_success: false,
        disable_event_publishing: false,
        culture: 'en-US'
      }
      
      setPayloadPreview(JSON.stringify(payload, null, 2))
      
      addLog('Depositing merged points...')
      const response = await depositMergedPoints(survivorId, depositDetails)
      
      addLog('✅ Points merge completed successfully')
      addLog(JSON.stringify(response, null, 2))
      setSuccess('Points merge completed successfully')
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Points merge failed'
      setError(errorMessage)
      addLog(`❌ Points merge error: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleIssueOffers = async () => {
    if (!survivorId) {
      setError('No survivor loaded. Fetch profiles first.')
      return
    }

    try {
      setIsLoading(true)
      addLog('Starting offers transfer...')
      
      // Count selected offers by offer_id
      const countsByOfferId: {[offerId: string]: number} = {}
      
      Object.entries(victimOffers).forEach(([userId, offers]) => {
        const checks = victimOfferChecks[userId] || {}
        const email = victimIdToEmail[userId] || userId
        
        // Ensure offers is an array
        const offersArray = Array.isArray(offers) ? offers : []
        
        offersArray.forEach(offer => {
          if (checks[offer.offer_instance_id]) {
            countsByOfferId[offer.offer_id] = (countsByOfferId[offer.offer_id] || 0) + 1
            addLog(`Selected offer from ${email}: ${offer.name || offer.offer_id}`)
          }
        })
      })
      
      const offersToIssue = Object.entries(countsByOfferId).map(([offer_id, quantity]) => ({
        offer_id,
        quantity
      }))
      
      if (offersToIssue.length === 0) {
        setError('No victim offers selected to issue')
        return
      }
      
      const payload = {
        user_id: survivorId,
        offers_to_issue: offersToIssue,
        retailer_id: "AAB85D10-D11A-4012-8465-C961AC49DC91",
        issue_exact_version: false,
        rollback_option: 0,
        culture: 'en-US'
      }
      
      setPayloadPreview(JSON.stringify(payload, null, 2))
      
      addLog('Issuing offers to survivor...')
      const response = await issueOffersBulk(survivorId, offersToIssue)
      
      addLog('✅ Offers transfer completed successfully')
      addLog(JSON.stringify(response, null, 2))
      setSuccess('Offers transfer completed successfully')
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Offers transfer failed'
      setError(errorMessage)
      addLog(`❌ Offers transfer error: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  const buildProfilePayload = (merged: ProfileData) => {
    const userPayload: any = {}
    
    BASIC_FIELDS.forEach(field => {
      if (field !== 'phone_number' && merged[field] != null) {
        userPayload[field] = merged[field]
      }
    })
    
    if (merged.tier != null) userPayload.tier = merged.tier
    if (merged.tier_system != null) userPayload.tier_system = merged.tier_system
    
    const userProfile: any = {}
    PREF_FIELDS.forEach(field => {
      if (merged[field] != null) {
        userProfile[field] = merged[field]
      }
    })
    
    if (Object.keys(userProfile).length) {
      userPayload.user_profile = userProfile
    }
    
    if (merged.phone_number) {
      userPayload.phone_numbers = [{ phone_number: merged.phone_number }]
    }
    
    return { user: userPayload }
  }

  const handleDeleteVictims = async () => {
    const victimIds = Object.keys(victimProfiles)
    if (!victimIds.length) {
      setError('No victim profiles loaded to delete')
      return
    }

    const victimList = victimIds.map(id => victimIdToEmail[id] || id).join(', ')
    const confirmed = window.confirm(
      `⚠️ DELETE VICTIMS?\n\nThis will permanently delete:\n${victimList}\n\nAre you sure?`
    )
    
    if (!confirmed) return

    try {
      setIsLoading(true)
      addLog('Starting victim deletion...')
      
      for (const victimId of victimIds) {
        const email = victimIdToEmail[victimId] || victimId
        addLog(`Deleting (forgetting) victim: ${email}`)
        
        try {
          const response = await forgetUser(victimId)
          addLog(`✅ Deleted ${email}: ${JSON.stringify(response)}`)
        } catch (err) {
          addLog(`❌ Error deleting ${email}: ${err instanceof Error ? err.message : 'Unknown error'}`)
        }
      }
      
      // Clear victim data after deletion
      setVictimProfiles({})
      setVictimIdToEmail({})
      setVictimCheckStates({})
      setVictimBalances({})
      setVictimOffers({})
      setVictimOfferChecks({})
      
      setSuccess('All victim profiles deleted successfully')
      addLog('✅ All victims deletion process completed')
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete victims'
      setError(errorMessage)
      addLog(`❌ Deletion error: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }


  return (
    <MergeContainer>
      <MergeTitle>
        🔄 Customer Profile Merge Tool
      </MergeTitle>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <InputSection>
        <FormGroup>
          <Label htmlFor="survivorEmail">Keep Email (Current User):</Label>
          <Input
            id="survivorEmail"
            type="email"
            value={survivorEmail}
            readOnly
            placeholder="No user selected"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="victimEmails">Remove Emails (comma separated):</Label>
          <Input
            id="victimEmails"
            type="text"
            value={victimEmails}
            onChange={(e) => setVictimEmails(e.target.value)}
            placeholder="remove1@example.com, remove2@example.com"
          />
        </FormGroup>
      </InputSection>

      <ButtonGroup>
        <Button 
          onClick={handleFetchProfiles} 
          primary 
          disabled={isLoading || !survivorEmail.trim()}
        >
          {isLoading ? 'Fetching...' : 'Fetch Profiles & Data'}
        </Button>
        
        <Button 
          onClick={handleMergeProfiles} 
          disabled={isLoading || !survivorProfile}
        >
          Merge Profiles
        </Button>
        
        <Button 
          onClick={handleMergePoints}
          disabled={isLoading || !Object.keys(victimBalances).length}
        >
          Merge Points
        </Button>
        
        <Button 
          onClick={handleIssueOffers}
          disabled={isLoading || !Object.keys(victimOffers).length}
        >
          Issue Offers
        </Button>
        
        <DangerButton 
          onClick={handleDeleteVictims}
          disabled={isLoading || !Object.keys(victimProfiles).length}
        >
          ☢️ Delete Removed Accounts
        </DangerButton>
      </ButtonGroup>

      {/* Profile Display Section */}
      {(survivorProfile || Object.keys(victimProfiles).length > 0) && (
        <SectionContainer>
          {/* Survivor Profile */}
          {survivorProfile && (
            <ProfileCard>
              <CardTitle>👑 Keep Profile</CardTitle>
              {renderFieldSection('Basic Information', BASIC_FIELDS, survivorProfile)}
              {renderFieldSection('Tier Status', TIER_FIELDS, survivorProfile)}
              {renderFieldSection('Preferences', PREF_FIELDS, survivorProfile)}
            </ProfileCard>
          )}

          {/* Victim Profiles */}
          {Object.keys(victimProfiles).length > 0 && (
            <ProfileCard>
              <CardTitle>🎯 Remove Profiles</CardTitle>
              {Object.entries(victimProfiles).map(([userId, profile]) => {
                const email = victimIdToEmail[userId] || userId
                return (
                  <div key={userId}>
                    <h4>Remove: {email}</h4>
                    {renderFieldSection('Basic Information', BASIC_FIELDS, profile, true, userId)}
                    {renderFieldSection('Tier Status', TIER_FIELDS, profile, true, userId)}
                    {renderFieldSection('Preferences', PREF_FIELDS, profile, true, userId)}
                  </div>
                )
              })}
            </ProfileCard>
          )}
        </SectionContainer>
      )}

      {/* Points Display Section */}
      {(Object.keys(survivorBalances).length > 0 || Object.keys(victimBalances).length > 0) && (
        <>
          <h3>Point Account Balances</h3>
          <SectionContainer>
            {/* Survivor Points */}
            {Object.keys(survivorBalances).length > 0 && (
              <ProfileCard>
                <CardTitle>👑 Keep Points</CardTitle>
                {renderPointsSection(survivorBalances, 'Keep')}
              </ProfileCard>
            )}

            {/* Victim Points */}
            {Object.keys(victimBalances).length > 0 && (
              <ProfileCard>
                <CardTitle>🎯 Remove Points</CardTitle>
                {Object.entries(victimBalances).map(([userId, balances]) => {
                  const email = victimIdToEmail[userId] || userId
                  return (
                    <div key={userId}>
                      <h4>Remove: {email}</h4>
                      {renderPointsSection(balances, `Remove ${email}`)}
                    </div>
                  )
                })}
                {renderVictimPointsSum()}
              </ProfileCard>
            )}
          </SectionContainer>
        </>
      )}

      {/* Offers Display Section */}
      {(survivorOffers.length > 0 || Object.keys(victimOffers).length > 0) && (
        <>
          <h3>Current Offers</h3>
          <SectionContainer>
            {/* Survivor Offers */}
            {survivorOffers.length > 0 && (
              <ProfileCard>
                <CardTitle>👑 Keep Offers ({survivorOffers.length})</CardTitle>
                {renderOffersSection(survivorOffers, false)}
              </ProfileCard>
            )}

            {/* Victim Offers */}
            {Object.keys(victimOffers).length > 0 && (
              <ProfileCard>
                <CardTitle>🎯 Remove Offers</CardTitle>
                {Object.entries(victimOffers).map(([userId, offers]) => {
                  const email = victimIdToEmail[userId] || userId
                  return (
                    <div key={userId}>
                      <h4>Victim: {email} ({offers.length} offers)</h4>
                      {renderOffersSection(offers, true, userId)}
                    </div>
                  )
                })}
              </ProfileCard>
            )}
          </SectionContainer>
        </>
      )}

      {/* Payload Preview */}
      {payloadPreview && (
        <div>
          <h3>Payload Preview</h3>
          <PayloadPreview>{payloadPreview}</PayloadPreview>
        </div>
      )}

      {/* Log Section */}
      {log && (
        <div>
          <h3>Operation Log</h3>
          <LogContainer>{log}</LogContainer>
        </div>
      )}

      <WarningMessage>
        ⚠️ <strong>Important:</strong> The merge tool performs permanent operations on customer data. 
        Always verify the survivor and victim emails before proceeding. The victim profiles will be 
        permanently deleted after the merge process.
      </WarningMessage>
    </MergeContainer>
  )
}

export default CustomerMerge